/**
 * Billing service for handling subscription management and pricing
 */

import { apiService } from './api';
import { extractErrorMessage, isAuthError } from '../utils/errorUtils';

export interface PricingTier {
  id: string;
  name: string;
  price_monthly: number;
  price_annual: number;
  features: {
    spaces: number; // -1 for unlimited
    api_calls_monthly: number; // -1 for unlimited
    support: string;
    collaboration: boolean;
    analytics: string;
    compliance?: boolean;
    sso?: boolean;
  };
}

export interface Subscription {
  tier: string;
  status: string;
  current_period_end?: number;
  cancel_at_period_end: boolean;
  price_id?: string;
  subscription_id?: string;
}

/** Order matches API `GET /billing/tiers` (tier_config.TIER_HIERARCHY). */
export const BILLING_TIER_ORDER = ['free', 'starter', 'pro', 'enterprise'] as const;

export type BillingTierId = (typeof BILLING_TIER_ORDER)[number];

export interface BillingTierConfigRow {
  tier: BillingTierId;
  display_name: string;
  price_monthly: number | null;
}

export interface UsageStats {
  tier: string;
  limits: {
    spaces: number;
    api_calls: number;
  };
  usage: {
    spaces_used: number;
    api_calls_this_month: number;
    period_start: string;
    period_end: string;
  };
  percentage_used: {
    spaces: number;
    api_calls: number;
  };
}

export interface CheckoutSession {
  checkout_url?: string;
  session_url?: string;
  session_id?: string;
}

export interface CreateCheckoutSessionRequest {
  price_id: string;
  tier: string;
  billing_cycle: 'monthly' | 'annual';
  coupon_code?: string;
  /** Browser origin for Stripe success/cancel redirects (must match auth storage origin). */
  return_origin?: string;
}

export interface BillingError {
  message: string;
  code?: string;
  requiresAuth?: boolean;
}

class BillingService {
  /**
   * Handle billing-specific errors with enhanced authentication support
   */
  private handleBillingError(error: any, operation: string = 'billing operation'): BillingError {
    // Check if this is an authentication error
    if (isAuthError(error)) {
      console.warn(`🔐 Authentication required for ${operation}`);
      return {
        message: 'Your session has expired. Please log in and try again.',
        code: 'AUTH_REQUIRED',
        requiresAuth: true
      };
    }

    // Handle rate limiting
    if (error.response?.status === 429) {
      return {
        message: 'Too many requests. Please wait a moment and try again.',
        code: 'RATE_LIMITED'
      };
    }

    // Handle server errors
    if (error.response?.status >= 500) {
      return {
        message: 'Server error. Please try again later or contact support if the problem persists.',
        code: 'SERVER_ERROR'
      };
    }

    // Handle billing-specific errors
    if (error.response?.status === 400) {
      const detail = error.response?.data?.detail || error.response?.data?.message;
      
      // Handle coupon-related errors
      if (detail?.includes('coupon') || detail?.includes('Coupon')) {
        return {
          message: detail || 'Invalid coupon code. Please check the code and try again.',
          code: 'INVALID_COUPON'
        };
      }
      
      if (detail?.includes('Invalid tier') || detail?.includes('Invalid price')) {
        return {
          message: 'The selected plan is not available. Please try a different plan.',
          code: 'INVALID_PLAN'
        };
      }
      if (detail?.includes('Stripe not configured')) {
        return {
          message: 'Billing is temporarily unavailable. Please try again later.',
          code: 'BILLING_UNAVAILABLE'
        };
      }
    }

    // Use the general error message extractor for other cases
    const message = extractErrorMessage(error, `Failed to complete ${operation}`);
    return { message };
  }

  /**
   * Attempt to refresh authentication token before retrying billing operations
   */
  private async refreshAuthAndRetry<T>(operation: () => Promise<T>, operationName: string): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      // If it's an auth error, let the user know they need to re-authenticate
      if (isAuthError(error)) {
        console.log(`🔄 Auth error in ${operationName}, user needs to re-authenticate`);
        // The API service interceptor will handle token removal
        // We'll let the error bubble up with proper auth context
      }
      
      throw error; // Re-throw the original error for proper handling
    }
  }

  /**
   * Tier display rows aligned with backend tier_config (prices, display names).
   */
  async getOrderedTierConfigs(): Promise<BillingTierConfigRow[]> {
    try {
      const rows = await apiService.get<unknown>('billing/tiers');
      if (!Array.isArray(rows)) {
        return [];
      }
      return BILLING_TIER_ORDER.map((tier, i) => {
        const raw = rows[i] as Record<string, unknown> | undefined;
        const display_name =
          typeof raw?.display_name === 'string' ? raw.display_name : tier;
        const pm = raw?.price_monthly;
        let price_monthly: number | null = null;
        if (pm !== null && pm !== undefined) {
          const n = typeof pm === 'number' ? pm : Number(pm);
          price_monthly = Number.isFinite(n) ? n : null;
        }
        return { tier, display_name, price_monthly };
      });
    } catch (error) {
      console.error('Error loading billing tier configs:', error);
      return [];
    }
  }

  /**
   * Get available pricing tiers
   */
  async getPricingTiers(): Promise<{ tiers: PricingTier[] }> {
    try {
      const response = await this.refreshAuthAndRetry(
        () => apiService.get('billing/tiers'),
        'pricing tiers retrieval'
      );
      return response;
    } catch (error) {
      const billingError = this.handleBillingError(error, 'pricing tiers retrieval');
      console.error('Error getting pricing tiers:', billingError);
      
      // Return fallback pricing if API fails
      return {
        tiers: [
          {
            id: 'free',
            name: 'Free',
            price_monthly: 0,
            price_annual: 0,
            features: {
              spaces: 1,
              api_calls_monthly: 5000,
              support: 'Community',
              collaboration: false,
              analytics: 'Basic',
            }
          },
          {
            id: 'starter',
            name: 'Starter',
            price_monthly: 9,
            price_annual: 90,
            features: {
              spaces: 3,
              api_calls_monthly: 25000,
              support: 'Email',
              collaboration: false,
              analytics: 'Growth',
            }
          },
          {
            id: 'pro',
            name: 'Pro',
            price_monthly: 29,
            price_annual: 290,
            features: {
              spaces: -1,
              api_calls_monthly: 250000,
              support: 'Priority',
              collaboration: false,
              analytics: 'Advanced',
            }
          },
          {
            id: 'enterprise',
            name: 'Enterprise',
            price_monthly: 0,
            price_annual: 0,
            features: {
              spaces: -1,
              api_calls_monthly: -1,
              support: 'Dedicated',
              collaboration: true,
              analytics: 'Full',
              compliance: true,
              sso: true,
            }
          }
        ]
      };
    }
  }

  /**
   * Get current user subscription
   */
  /**
   * Verify Stripe Checkout Session and sync tier to Supabase (webhook recovery).
   */
  async confirmCheckoutSession(
    sessionId: string
  ): Promise<{ tier: string; synced: boolean; subscription_id?: string }> {
    return this.refreshAuthAndRetry(
      () => apiService.post('billing/confirm-checkout-session', { session_id: sessionId }),
      'confirm checkout session'
    );
  }

  /**
   * Infer tier from Stripe active subscriptions for stripe_customer_id and write Supabase.
   */
  async syncSubscriptionFromStripe(): Promise<{
    tier: string;
    synced: boolean;
    subscription_id?: string;
  }> {
    return this.refreshAuthAndRetry(
      () => apiService.post('billing/sync-subscription-from-stripe', {}),
      'sync subscription from stripe'
    );
  }

  async getCurrentSubscription(): Promise<Subscription> {
    try {
      const response = await this.refreshAuthAndRetry(
        () => apiService.get('billing/subscription'),
        'subscription retrieval'
      );
      return {
        tier: typeof response?.tier === 'string' ? response.tier : 'free',
        status: typeof response?.status === 'string' ? response.status : 'none',
        cancel_at_period_end: Boolean(response?.cancel_at_period_end),
        current_period_end: response?.current_period_end,
        price_id: response?.price_id,
        subscription_id:
          typeof response?.subscription_id === 'string'
            ? response.subscription_id
            : undefined,
      };
    } catch (error) {
      const billingError = this.handleBillingError(error, 'subscription information retrieval');
      console.error('Error getting subscription:', billingError);
      
      // For subscription info, we can fail gracefully
      if (billingError.requiresAuth) {
        console.warn('Authentication required for subscription info - returning free tier default');
        return {
          tier: 'free',
          status: 'none',
          cancel_at_period_end: false
        };
      }
      
      throw new Error(billingError.message);
    }
  }

  /**
   * Get current usage statistics
   */
  async getUsageStats(): Promise<UsageStats> {
    const response = await apiService.get('billing/usage');
    return response; // apiService.get already returns response.data
  }

  /**
   * Create Stripe checkout session for upgrade
   */
  async createCheckoutSession(request: CreateCheckoutSessionRequest): Promise<CheckoutSession> {
    try {
      const payload = {
        ...request,
        return_origin:
          typeof window !== 'undefined' ? window.location.origin : request.return_origin,
      };
      const response = await this.refreshAuthAndRetry(
        () => apiService.post('billing/upgrade', payload),
        'checkout session creation'
      );
      return response; // apiService.post already returns response.data
    } catch (error) {
      const billingError = this.handleBillingError(error, 'upgrade checkout session creation');
      console.error('Error creating checkout session:', billingError);
      
      // Re-throw with enhanced error info
      const enhancedError = new Error(billingError.message);
      (enhancedError as any).billingCode = billingError.code;
      (enhancedError as any).requiresAuth = billingError.requiresAuth;
      (enhancedError as any).originalError = error;
      throw enhancedError;
    }
  }

  /**
   * Upgrade to a specific tier
   * @param tier Target tier (starter, pro, enterprise)
   * @param billingCycle monthly or annual
   * @param couponCode Optional coupon code for discount
   */
  async upgradeTier(tier: string, billingCycle: 'monthly' | 'annual' = 'monthly', couponCode?: string): Promise<string> {
    try {
      console.log('🚀 Starting upgrade process for tier:', tier);
      
      // CRITICAL FIX: Store session data before Stripe redirect to prevent session loss
      this.storeSessionDataBeforeCheckout();
      
      // Send tier and billing cycle to backend - backend handles price ID mapping
      const checkoutSession = await this.createCheckoutSession({
        price_id: '', // Backend will determine the correct price ID from tier + billing_cycle
        tier,
        billing_cycle: billingCycle,
        coupon_code: couponCode,
      });

      console.log('✅ Checkout session created, redirecting to Stripe...');

      const pendingSid = checkoutSession.session_id;
      if (pendingSid) {
        try {
          sessionStorage.setItem('memara_pending_checkout_session_id', pendingSid);
        } catch {
          /* ignore storage failures */
        }
      }

      // Redirect to Stripe checkout
      if (checkoutSession && checkoutSession.session_url) {
        window.location.href = checkoutSession.session_url;
      } else if (checkoutSession && checkoutSession.checkout_url) {
        window.location.href = checkoutSession.checkout_url;
      } else {
        console.error('No valid URL in checkout session:', checkoutSession);
        throw new Error('Invalid checkout session response');
      }
      return checkoutSession.session_id || '';
    } catch (error: any) {
      // Clean up stored session data on error
      this.clearStoredSessionData();
      
      // Check if this is an authentication error
      if (error.requiresAuth) {
        console.error('Authentication required for upgrade:', error.message);
        // Optionally trigger a re-authentication flow here
        // For now, just throw the error with the auth flag
        throw new Error(`${error.message} Please refresh the page and try again.`);
      }
      
      console.error('Error upgrading tier:', error);
      throw error;
    }
  }

  /**
   * CRITICAL FIX: Store session data before Stripe checkout to prevent session loss
   */
  private storeSessionDataBeforeCheckout(): void {
    try {
      const sessionData = {
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        // Store flag for session recovery
        pendingUpgrade: true,
        upgradeStartTime: Date.now()
      };
      
      // Store in both localStorage and sessionStorage for redundancy
      localStorage.setItem('memara_pre_checkout_session', JSON.stringify(sessionData));
      sessionStorage.setItem('memara_pre_checkout_session', JSON.stringify(sessionData));
      
      console.log('💾 Session data stored before checkout');
    } catch (error) {
      console.warn('⚠️ Failed to store session data:', error);
    }
  }

  /**
   * Clean up stored session data
   */
  private clearStoredSessionData(): void {
    try {
      localStorage.removeItem('memara_pre_checkout_session');
      sessionStorage.removeItem('memara_pre_checkout_session');
      console.log('🧹 Stored session data cleared');
    } catch (error) {
      console.warn('⚠️ Failed to clear session data:', error);
    }
  }

  /**
   * Check if user is returning from checkout and needs session recovery
   */
  static checkForSessionRecovery(): boolean {
    try {
      // Check URL parameters for checkout return
      const urlParams = new URLSearchParams(window.location.search);
      const hasSessionId = urlParams.has('session_id');
      const isBillingSuccess = window.location.pathname === '/billing/success';
      
      // Check stored session data
      const storedData = localStorage.getItem('memara_pre_checkout_session') || 
                        sessionStorage.getItem('memara_pre_checkout_session');
      
      if ((hasSessionId || isBillingSuccess) && storedData) {
        const sessionData = JSON.parse(storedData);
        const timeSinceCheckout = Date.now() - sessionData.upgradeStartTime;
        
        // If less than 30 minutes since checkout started, attempt recovery
        if (timeSinceCheckout < 30 * 60 * 1000) {
          console.log('🔄 User returning from checkout, session recovery needed');
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.warn('⚠️ Failed to check for session recovery:', error);
      return false;
    }
  }

  /**
   * Handle enterprise plan contact
   */
  contactEnterpriseSales(): void {
    const subject = encodeURIComponent('Enterprise Plan Inquiry');
    const body = encodeURIComponent('Hi, I\'m interested in learning more about Memara\'s Enterprise plan. Please contact me with more details.');
    window.open(`mailto:sales@memara.io?subject=${subject}&body=${body}`);
  }

  /**
   * Get tier display information
   */
  getTierDisplayInfo(tier: string): { name: string; color: string; description: string } {
    const normalizedTier = tier.toLowerCase();
    const tierInfo: Record<string, { name: string; color: string; description: string }> = {
      free: {
        name: 'Free',
        color: 'purple',
        description: 'Perfect for trying out AI memory'
      },
      starter: {
        name: 'Starter',
        color: 'orange',
        description: 'For growing projects with higher limits'
      },
      pro: {
        name: 'Pro',
        color: 'indigo',
        description: 'Professional AI memory for power users'
      },
      team: {
        name: 'Team',
        color: 'emerald',
        description: 'Collaborative AI memory for teams'
      },
      enterprise: {
        name: 'Enterprise',
        color: 'slate',
        description: 'Enterprise-grade with full compliance'
      }
    };

    if (tierInfo[normalizedTier]) {
      return tierInfo[normalizedTier];
    }

    return {
      name: normalizedTier.charAt(0).toUpperCase() + normalizedTier.slice(1),
      color: 'slate',
      description: 'Custom plan',
    };
  }

  /**
   * Check if user can access a feature based on their tier
   */
  canAccessFeature(userTier: string, feature: string): boolean {
    const tierFeatures: Record<string, string[]> = {
      free: ['basic_search', 'memory_storage', 'integrations'],
      starter: ['basic_search', 'memory_storage', 'integrations', 'advanced_search'],
      pro: ['basic_search', 'memory_storage', 'integrations', 'advanced_search', 'email_support', 'analytics'],
      team: ['basic_search', 'memory_storage', 'integrations', 'advanced_search', 'email_support', 'analytics', 'collaboration', 'priority_support'],
      enterprise: ['basic_search', 'memory_storage', 'integrations', 'advanced_search', 'email_support', 'analytics', 'collaboration', 'priority_support', 'compliance', 'sso', 'audit_trails']
    };

    return tierFeatures[userTier]?.includes(feature) || false;
  }

  /**
   * Get upgrade suggestions based on current usage
   */
  getUpgradeSuggestions(usage: UsageStats): { suggested_tier?: string; reasons: string[] } {
    const suggestions: string[] = [];
    let suggestedTier: string | undefined;

    // Check space usage
    if (usage.percentage_used.spaces > 80) {
      suggestions.push('You\'re approaching your space limit');
      if (usage.tier === 'free') {
        suggestedTier = 'pro';
      }
    }

    // Check API usage
    if (usage.percentage_used.api_calls > 80) {
      suggestions.push('You\'re approaching your API call limit');
      if (usage.tier === 'free') {
        suggestedTier = 'pro';
      } else if (usage.tier === 'pro') {
        suggestedTier = 'team';
      }
    }

    // Suggest team features
    if (usage.tier === 'pro' && usage.usage.spaces_used > 5) {
      suggestions.push('Consider Team plan for collaboration features');
      suggestedTier = 'team';
    }

    return { suggested_tier: suggestedTier, reasons: suggestions };
  }

  /**
   * Cancel current subscription
   */
  async cancelSubscription(): Promise<any> {
    try {
      const response = await this.refreshAuthAndRetry(
        () => apiService.post('billing/subscription/cancel'),
        'subscription cancellation'
      );
      return response;
    } catch (error) {
      const billingError = this.handleBillingError(error, 'subscription cancellation');
      console.error('Error canceling subscription:', billingError);
      
      const enhancedError = new Error(billingError.message);
      (enhancedError as any).requiresAuth = billingError.requiresAuth;
      throw enhancedError;
    }
  }

  /**
   * Create Stripe Customer Portal session
   * Returns URL to redirect user to Stripe-hosted portal
   */
  async createCustomerPortalSession(): Promise<{ url: string }> {
    try {
      const body =
        typeof window !== 'undefined'
          ? { return_origin: window.location.origin }
          : {};
      const response = await this.refreshAuthAndRetry(
        () => apiService.post('billing/customer-portal', body),
        'customer portal session creation'
      );
      return response;
    } catch (error) {
      const billingError = this.handleBillingError(error, 'customer portal access');
      console.error('Error creating customer portal session:', billingError);
      
      const enhancedError = new Error(billingError.message);
      (enhancedError as any).requiresAuth = billingError.requiresAuth;
      throw enhancedError;
    }
  }
}

export const billingService = new BillingService(); 